"use no memo";

import { useForm, useWatch } from "react-hook-form";
import { sendNewMessageUserGuestSchema, SendNewMessageUserGuestType } from "../../api/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCreateLeadConversationMutation } from "@/components/modules/chat/api/mutations/useCreateLeadConversationMutation";
import { useModal } from "@/components/providers/modal-provider/ModalProvider";
import { useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "@/constants/queryKeys";
import { User } from "@/types/me";
import { useState } from "react";
import { useTranslations } from "next-intl";
type UseSendMessageUserGuestProps = {
  recipientId: string;
  realEstateId?: string;
  onLoginSuccess?: ({
    user,
    conversationId
  }: {
    user: User;
    conversationId: string;
  }) => void;
};
export function useSendMessageUserGuest({
  recipientId,
  realEstateId,
  onLoginSuccess
}: UseSendMessageUserGuestProps) {
  const t = useTranslations("modules.auth.sendMessage");
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<"message" | "login">("message");
  const [messageValidationAttempted, setMessageValidationAttempted] = useState(false);
  const [nameValidationAttempted, setNameValidationAttempted] = useState(false);
  const queryClient = useQueryClient();
  const {
    closeModalById,
    modalStack
  } = useModal();
  const {
    mutate,
    isPending
  } = useCreateLeadConversationMutation();
  const form = useForm<SendNewMessageUserGuestType>({
    resolver: zodResolver(sendNewMessageUserGuestSchema),
    defaultValues: {
      email: "",
      message: "",
      name: "",
      password: ""
    }
  });
  const messageField = useWatch({
    control: form.control,
    name: "message"
  });
  const nameField = useWatch({
    control: form.control,
    name: "name"
  });
  function onSubmit(data: SendNewMessageUserGuestType) {
    mutate({
      email: data.email,
      message: data.message,
      name: data.name,
      password: data.password,
      recipientId,
      realEstateId
    }, {
      onSuccess: data_0 => {
        if (data_0 && data_0.data.user) {
          queryClient.setQueryData([QueryKeys.SESSION], data_0.data.user);
          queryClient.invalidateQueries({
            queryKey: [QueryKeys.REAL_ESTATE]
          });
          queryClient.invalidateQueries({
            queryKey: [QueryKeys.LOCATIONS]
          });
        }
        form.reset();
        closeModalById(modalStack[modalStack.length - 1]?.id || "");
        if (onLoginSuccess) {
          onLoginSuccess({
            user: data_0.data.user,
            conversationId: data_0.data.conversation.id
          });
        }
      },
      onError: (error_0: any) => {
        setError(error_0?.response?.data?.error || error_0?.response?.data?.message || t("userAlreadyExists"));
      }
    });
  }
  return {
    form,
    onSubmit,
    isPending,
    error,
    mode,
    setMode,
    messageField,
    messageValidationAttempted,
    setMessageValidationAttempted,
    nameValidationAttempted,
    setNameValidationAttempted,
    nameField,
    t
  };
}